import { useNavigate } from "@solidjs/router";
import { createMemo } from "solid-js";
import { useI18n } from "@/api/i18n";
import { CompanyLayoutMainContentContainer } from "@/components/layout/CompanyLayout";
import { Container } from "@/components/shared/Container";
import { MetaTitleWithSeparator } from "@/domains/business/seo";
import { LeaveConfirmationDialog } from "@/domains/company/active-search/components/LeaveConfirmationDialog";
import {
  useFreelanceSearchActions,
  useFreelanceSearchStore,
} from "@/domains/company/active-search/store/freelance-search-store";
import {
  buildPathWithQueryParams,
  filtersToFormDefaultValues,
  freelancerFilterFormToQueryParams,
} from "@/domains/company/active-search/utils/filterParams";
import { withoutRemovedCategory } from "@/domains/company/active-search/utils/sanitize-search-filters";
import { AiChatProvider } from "@/domains/job-offer/ai/store/AiChatProvider";
import { AiFromProfilesGenerationModal } from "./components/AiFromProfilesGenerationModal";

export default function CompanySearchFreelancerNewProposalRoute() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [storeState] = useFreelanceSearchStore();
  const actions = useFreelanceSearchActions();

  // The results page rewrites `proposalPreFillFilters` from the URL without
  // going through `deserialize`, so a removed category is dropped here too.
  const filters = createMemo(() =>
    withoutRemovedCategory(
      storeState.proposalPreFillFilters ?? storeState.initialResultFilters,
    ),
  );

  const freelancerIds = createMemo(() =>
    storeState.selectedFreelancers.map((f) => f.id),
  );

  // Closing without generating returns to the results with the same selection.
  // Two points are load-bearing: the target is an explicit path, never
  // `navigate(-1)` (a numeric `e.to` does not match the leave guard's excluded
  // routes, so it would open the confirmation and «Conferma» would empty the
  // selection); and `replace: true`, otherwise Back from the results lands on
  // this page again, which reopens the dialog on mount, in a loop.
  const handleClose = () => {
    navigate(
      buildPathWithQueryParams(
        "/profile/company/search/freelancers/result",
        freelancerFilterFormToQueryParams(
          filtersToFormDefaultValues(filters() ?? {}),
        ),
      ),
      { replace: true },
    );
  };

  return (
    <AiChatProvider>
      <MetaTitleWithSeparator>
        {t("jobOffers.create.title")}
      </MetaTitleWithSeparator>
      <CompanyLayoutMainContentContainer>
        <Container size="4xl" class="mb-6 w-full">
          <h1 class="text-3xl font-bold text-gray-900 mb-2">
            {t("company.search.freelancers.new_proposal.title")}
          </h1>
          <p class="text-gray-600 text-base">
            {t("company.search.freelancers.new_proposal.description")}
          </p>
        </Container>

        <AiFromProfilesGenerationModal
          open={true}
          onClose={handleClose}
          freelancerIds={freelancerIds()}
          selectedFreelancers={storeState.selectedFreelancers}
          filters={filters() ?? {}}
        />
      </CompanyLayoutMainContentContainer>

      <LeaveConfirmationDialog
        when={() => storeState.selectedFreelancers.length > 0}
        onConfirm={() => actions.clearFreelancers()}
        excludedRoutes={["result", "send-proposal", "refine"]}
      />
    </AiChatProvider>
  );
}
